import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormHelperText,
  Grid,
  styled,
  TextField
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Paragraph } from "app/components/Typography";
import { ErrorContext } from "app/contexts/ErrorContext";
import useCheckCandidateExists from "app/hooks/candidate/useCheckCandidateExists";
import { useGenerateOTP } from "app/hooks/otp/useGenerateOTP";
import { useVerifyOTP } from "app/hooks/otp/useVerifyOTP";
import useAuth from "app/hooks/useAuth";
import { AppRoutes, PhoneRegex } from "app/utils/global_constants";
import { Formik } from "formik";
import { useContext, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import posting_photo from "../../../assets/illustrations/posting_photo.svg";
import PasswordTextField from "./shared/PasswordTextField";
import PrivacyPolicyDialog from "./shared/PrivacyPolicyDialog";
import TCDialog from "./shared/TCDialog";
import VerifyOTPDialog from "./shared/VerifyOTPDialog";
// STYLED COMPONENTS
const FlexBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center"
}));

const JustifyBox = styled(FlexBox)(() => ({
  justifyContent: "center"
}));

const ContentBox = styled(JustifyBox)(() => ({
  height: "100%",
  padding: "32px",
  background: "rgba(0, 0, 0, 0.01)"
}));

const DialogButton = styled(Button)(() => ({ fontSize: "12px", fontWeight: "bold" }));

const JWTRegister = styled(JustifyBox)(() => ({
  background: "#1A2038",
  minHeight: "100vh !important",
  "& .card": {
    maxWidth: 800,
    minHeight: 400,
    margin: "1rem",
    display: "flex",
    borderRadius: 12,
    alignItems: "center"
  }
}));

// initial login credentials
const initialValues = {
  jp_email: "",
  jp_password: "",
  jp_candidate_name: "",
  remember: false,
  jp_contactno: ""
};

// form field validation schema
const validationSchema = Yup.object().shape({
  jp_candidate_name: Yup.string().required("Full name is required"),
  remember: Yup.boolean()
    .oneOf([true], "Please accept Terms and Conditions before proceeding")
    .required("Please accept Terms and Conditions before proceeding"),
  jp_password: Yup.string()
    .min(8, "Password must be 8 character length")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
      "Password must include an uppercase letter, a lowercase letter, a number, and a special character."
    )
    .required("Password is required"),
  jp_contactno: Yup.string()
    .matches(PhoneRegex, "Enter a valid contact no")
    .required("Contact number is required"),
  jp_email: Yup.string()
    .required("Email is required")
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Invalid Email"
    )
    .email("Invalid Email address")
});

export default function JwtRegisterCandidate() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { success, error, message, severity, setErrorState, setSuccessState, clearErrorState } =
    useContext(ErrorContext);
  const { register, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [otp, setOTP] = useState();
  const [otpError, setOTPError] = useState(false);

  const [registerValues, setRegisterValues] = useState();

  const [loading, setLoading] = useState(false);
  // constrols dialog that displays the terms and conditions
  const [showTC, setShowTC] = useState(false);
  // controls dialog that displays the privacy policy
  const [showPP, setShowPP] = useState(false);
  // controls otp dialog
  const [showVerifyOTP, setShowVerifyOTP] = useState(false);

  //** Register Candidate */
  const registerCandidate = async (values) => {
    setLoading(true);

    const registerState = await register(values, "candidate");
    if (registerState && registerState.error) {
      setErrorState(true, registerState.message, "error");
    }

    if (registerState && registerState.error === false) {
      await refreshUser();
      setSuccessState(true, "You have registered successfully. Redirecting.....");
    }

    setLoading(false);
    setShowVerifyOTP(false);
  };

  //** OTP Generation and Verfication */
  const { mutate: generatedOTP, isPending: isGeneratingOTP } = useGenerateOTP(() =>
    setShowVerifyOTP(true)
  );

  const { mutate: verifyOTP } = useVerifyOTP({
    onSuccess: () => registerCandidate({ ...registerValues, jp_is_email_verified: 1 }),
    onError: () => setOTPError(true)
  });

  const onCheckCandidateSuccess = (data, _) => {
    if (data) {
      setErrorState(true, "Candidate already exists", "error");
    } else {
      generatedOTP({
        email: registerValues.jp_email,
        type: "verify-email",
        name: registerValues.jp_company_name
      });
    }
  };

  const onCheckCandidateFail = () => {
    setErrorState(true, "Cannot register at the moment", "error");
  };

  const { mutate: checkCandidateExists } = useCheckCandidateExists({
    onSuccess: onCheckCandidateSuccess,
    onError: onCheckCandidateFail
  });

  const handleFormSubmit = async (values) => {
    setRegisterValues(values);
    checkCandidateExists({ email: values.jp_email });
  };

  const loginLink = location.pathname.includes("company")
    ? "/session/login/company"
    : "/session/login/candidate";

  return (
    <JWTRegister>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={error || success}
        onClose={() => {
          const redirectToCheckout = searchParams.get("redirectToCheckout");
          const plan = searchParams.get("plan");
          if (success && !redirectToCheckout) {
            navigate(AppRoutes.ROUTE_DASHBOARD);
          } else if (success && redirectToCheckout && plan) {
            navigate(`${AppRoutes.ROUTE_CHECKOUT}?plan=${plan}&type=candidate`);
          }
          clearErrorState();
        }}
        autoHideDuration={3000}
        color={severity}
      >
        <Alert severity={severity}>{message}</Alert>
      </Snackbar>
      {/** Terms and Condition */}
      <TCDialog open={showTC} handleClose={() => setShowTC(false)} />
      {/** Privacy Policy*/}
      <PrivacyPolicyDialog open={showPP} handleClose={() => setShowPP(false)} />
      {/** Verify OTP */}
      <VerifyOTPDialog
        open={showVerifyOTP}
        otpError={otpError}
        verifyState={loading}
        resendState={isGeneratingOTP}
        handleOTPChange={(otp) => setOTP(otp)}
        handleVerifyOTP={() => verifyOTP({ email: registerValues.jp_email, otp: otp })}
        handleResendOTP={() =>
          generatedOTP({
            email: registerValues.jp_email,
            type: "verify-email",
            name: registerValues.jp_candidate_name
          })
        }
        handleClose={() => setShowVerifyOTP(false)}
      />
      <Card className="card">
        <Grid container>
          <Grid item sm={6} xs={12}>
            <ContentBox>
              <img width="100%" alt="Register" src={posting_photo} />
            </ContentBox>
          </Grid>

          <Grid item sm={6} xs={12}>
            <Box p={4} height="100%">
              <Formik
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleChange,
                  handleBlur,
                  handleSubmit
                }) => (
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      name="jp_candidate_name"
                      label="Full Name"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.jp_candidate_name}
                      onChange={(e) => {
                        const filteredValue = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                        setFieldValue("jp_candidate_name", filteredValue);
                      }}
                      helperText={touched.jp_candidate_name && errors.jp_candidate_name}
                      error={Boolean(errors.jp_candidate_name && touched.jp_candidate_name)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      name="jp_email"
                      label="Email"
                      autoComplete="email"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.jp_email}
                      onChange={handleChange}
                      helperText={touched.jp_email && errors.jp_email}
                      error={Boolean(errors.jp_email && touched.jp_email)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      name="jp_contactno"
                      label="Contact No"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.jp_contactno}
                      onChange={handleChange}
                      inputProps={{ maxLength: 25 }}
                      helperText={touched.jp_contactno && errors.jp_contactno}
                      rules={{
                        pattern: { value: PhoneRegex, message: "Please enter valid phone number" }
                      }}
                      error={Boolean(errors.jp_contactno && touched.jp_contactno)}
                      sx={{ mb: 3 }}
                    />
                    <PasswordTextField
                      name={"jp_password"}
                      value={values.jp_password}
                      onChange={handleChange}
                      helperText={touched.jp_password && errors.jp_password}
                      errors={Boolean(errors.jp_password && touched.jp_password)}
                      onBlur={handleBlur}
                    />

                    <FlexBox gap={1} alignItems="center">
                      <Checkbox
                        size="small"
                        name="remember"
                        onChange={handleChange}
                        checked={values.remember}
                        sx={{ padding: 0 }}
                      />

                      <Paragraph fontSize={13}>
                        I have read and agree to the terms of service.
                      </Paragraph>
                    </FlexBox>
                    {errors.remember && (
                      <FormHelperText sx={{ color: "red" }}>{errors.remember}</FormHelperText>
                    )}

                    <Box display={"flex"} gap={2} mt={2}>
                      <DialogButton
                        sx={{ flex: 1 }}
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => setShowTC(true)}
                      >
                        View Terms & Conditions
                      </DialogButton>
                      <DialogButton
                        sx={{ flex: 1 }}
                        size="small"
                        variant="outlined"
                        onClick={() => setShowPP(true)}
                      >
                        View Privacy Policy
                      </DialogButton>
                    </Box>

                    <LoadingButton
                      type="submit"
                      color="primary"
                      fullWidth
                      loading={isGeneratingOTP && !showVerifyOTP}
                      variant="contained"
                      sx={{ mb: 2, mt: 2 }}
                    >
                      Register
                    </LoadingButton>

                    <Paragraph sx={{ textAlign: "center" }}>
                      Already have an account?
                      <NavLink to={loginLink} style={{ color: "#1976D2", marginLeft: 5 }}>
                        Login
                      </NavLink>
                    </Paragraph>
                  </form>
                )}
              </Formik>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </JWTRegister>
  );
}
